import { Suspense, useState } from "react";
import { Button, Col, Form, Row, Stack } from "react-bootstrap";
import { axiosInstance, useAuth } from "../../auth/Auth";
import { Await, useNavigate, useRevalidator, useRouteLoaderData } from "react-router-dom";
import { toast } from "react-toastify";
import SettingsTwoFactorSetupModal from "./SettingsTwoFactorSetupModal";
import SettingsTwoFactorDisableModal from "./SettingsTwoFactorDisableModal";
import RATooltip from "../../components/RATooltip";

export default function SettingsPassword() {
    const navigate = useNavigate();
    const { userInfo, remainingBackupCodes } = useRouteLoaderData("root");
    const { onLogout } = useAuth();
    const revalidator = useRevalidator();

    const [updatingPassword, setUpdatingPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [oldPassword, setOldPassword] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");

    const lengthValid = password.length >= 8;
    const uppercaseValid = /[A-Z]/.test(password);
    const lowercaseValid = /[a-z]/.test(password);
    const digitValid = /\d/.test(password);
    const passwordsMatch = password === password2;
    const isValid = lengthValid && uppercaseValid && lowercaseValid && digitValid;

    const [otpUri, setOtpUri] = useState(null);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);

    const handleUpdatePassword = async event => {
        event.preventDefault();
        setUpdatingPassword(true);

        try {
            await axiosInstance.put("/users/update_password/", {
                old_password: oldPassword,
                new_password_1: password,
                new_password_2: password2,
            });
            toast.success("Your password was reset successfully");
            navigate("/login", { replace: true });
        } catch (error) {
            if (error?.response?.status === 400 || error?.response?.status === 401) setErrorMessage(error.response.data.detail);
            else setErrorMessage("Unexpected error occured. Please try again later or contact us at tech@radioanalyzer.com");
        }
        setUpdatingPassword(false);
    };

    const handleChangeTwoFactor = async enable => {
        if (enable) {
            await generateTwoFactor();
        } else {
            setShowDisableModal(true);
        }
    };

    const generateTwoFactor = async () => {
        try {
            const response = await axiosInstance.get("/2fa/generate");
            setOtpUri(response.data);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to generate two-factor authentication");
        }
    };

    const logoutAllDevices = async () => {
        try {
            await axiosInstance.get("/logout_all");
            onLogout();
        } catch (error) {
            toast.error("Failed to log out from all devices. Please contact support@radioanalyzer.com");
        }
    };

    const enableTwoFactor = async code => {
        try {
            const response = await axiosInstance.post("/2fa/enable", { code });
            toast.success("Two-Factor Authentication enabled successfully");
            setBackupCodes(response.data.backup_codes);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to enable two-factor authentication");
        }
    };

    const disableTwoFactor = async (password, code) => {
        try {
            await axiosInstance.post("/2fa/disable", { password, code });
            toast.success("Two-Factor Authentication disabled successfully");
            onLogout();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to disable two-factor authentication");
        }
    };

    return (
        <>
            <SettingsTwoFactorSetupModal
                otpUri={otpUri}
                onClose={() => {
                    setOtpUri(null);
                    revalidator.revalidate();
                }}
                onComplete={enableTwoFactor}
                backupCodes={backupCodes}
            />
            <SettingsTwoFactorDisableModal
                show={showDisableModal}
                onClose={() => {
                    setShowDisableModal(false);
                    revalidator.revalidate();
                }}
                onComplete={disableTwoFactor}
            />
            <Row>
                <Col className="bg-white p-4">
                    <h2 className="text-center text-lg-start">CHANGE PASSWORD</h2>
                    <p className="text-center text-lg-start">All three fields are necessary.</p>
                    <Form onSubmit={handleUpdatePassword} className="row" noValidate>
                        <Col xs={12} lg={"auto"}>
                            <Form.Control
                                className="mb-3"
                                value={oldPassword}
                                type="password"
                                placeholder="Old password"
                                name={"old_password"}
                                onChange={e => setOldPassword(e.target.value)}
                            />
                            <Form.Control
                                className="mb-3"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                type="password"
                                name="password"
                                placeholder="New password"
                                isValid={isValid}
                            />
                            <Form.Control
                                className="mb-3"
                                value={password2}
                                onChange={e => setPassword2(e.target.value)}
                                type="password"
                                name="password2"
                                placeholder="Confirm new password"
                                isValid={password && passwordsMatch}
                            />
                            <Col>
                                <ul className="mb-4 text-danger">
                                    <li className={lengthValid ? "text-success" : ""}>Must contain at least 8 characters</li>
                                    <li className={uppercaseValid ? "text-success" : ""}>Must contain at least 1 uppercase letter</li>
                                    <li className={lowercaseValid ? "text-success" : ""}>Must contain at least 1 lowercase letter</li>
                                    <li className={digitValid ? "text-success" : ""}>Must contain at least 1 digit</li>
                                    <li className={password && passwordsMatch ? "text-success" : ""}>Passwords must match</li>
                                </ul>
                            </Col>
                            {errorMessage && (
                                <Col xs={12} className="mb-2">
                                    <span className="text-danger text-uppercase">{errorMessage}</span>
                                </Col>
                            )}
                            <Col xs={12} className="text-center text-lg-start">
                                <Button
                                    className={"btn-ra-dark-blue text-light fw-semibold text-uppercase text-center"}
                                    type={"submit"}
                                    disabled={updatingPassword || !oldPassword || !isValid || !passwordsMatch}>
                                    Update new password
                                </Button>
                            </Col>
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr />
            <Row>
                <Col className="bg-white p-4">
                    <h2 className="text-center text-lg-start">Security</h2>
                    <Stack gap={3} className="col-12 col-lg-6 col-xxl-4">
                        <Suspense>
                            <Await resolve={userInfo}>
                                {user => (
                                    <>
                                        <Stack direction="horizontal" className=" justify-content-between">
                                            <Stack direction="horizontal" gap={2}>
                                                <span>Two-Factor Authentication</span>
                                                <RATooltip>
                                                    Two-factor authentication adds an extra security step by requiring a one-time code from your phone in
                                                    addition to your password to log in.
                                                </RATooltip>
                                            </Stack>
                                            <Form.Switch
                                                id="2fa-switch"
                                                className="form-switch-lg"
                                                checked={user.data.two_factor_enabled}
                                                onChange={async e => await handleChangeTwoFactor(e.target.checked)}
                                            />
                                        </Stack>
                                        {user.data.two_factor_enabled && (
                                            <Suspense>
                                                <Await resolve={remainingBackupCodes}>
                                                    {codes => (
                                                        <Stack direction="horizontal" className=" justify-content-between">
                                                            <Stack direction="horizontal" gap={2}>
                                                                <span>Remaining Backup Codes</span>
                                                                <RATooltip>
                                                                    Backup codes are one-time use codes that let you sign in if you can’t access your
                                                                    authenticator app. If you’re running out, disable and re-enable two-factor authentication to
                                                                    generate new codes.
                                                                </RATooltip>
                                                            </Stack>
                                                            <span>{codes.data}</span>
                                                        </Stack>
                                                    )}
                                                </Await>
                                            </Suspense>
                                        )}
                                    </>
                                )}
                            </Await>
                        </Suspense>
                        <Stack direction="horizontal" className=" justify-content-between">
                            <Stack direction="horizontal" gap={2}>
                                <span>Suspect unusual activity?</span>
                                <RATooltip>Ends all active sessions to help secure your account if you notice unusual activity.</RATooltip>
                            </Stack>
                            <Button variant="ra-yellow" onClick={logoutAllDevices}>
                                Log out from all devices
                            </Button>
                        </Stack>
                    </Stack>
                </Col>
            </Row>
        </>
    );
}
